import { ReactElement, ReactNode } from 'react';
import ConfigEntry from './ConfigEntry';
import AccordionIcon from './AccordionIcon';
import { useFormContext } from 'react-hook-form';
import { EditOperationMode, isOperationLocked } from './editOperationUtils';

interface SessionConfigsListProps {
  session: any;
  isEdit: boolean;
  configControls: any;
  showAllRuns: boolean;
  setShowAllRuns: (show: boolean) => void;
  updateNextConfigId: any;
  plannedRunConfigIds: any[];
  setPlannedRunConfigIds: any;
  plannedRunNumbers: Record<number, number>;
  activeRunId: string | null;
  setActiveRunId: (id: string | null) => void;
  invalidateSession: any;
  reorderConfigLetters: () => void;
  configMovesToSave: { [key: string]: () => Promise<void> };
  changedConfigIds: Set<string>;
  setChangedConfigIds: (ids: Set<string>) => void;
  editOpMode: EditOperationMode;
  setEditOpMode: (mode: EditOperationMode) => void;
  onReprocessRun?: () => void;
  onEditConfig?: () => void;
  onSaveConfig?: () => void;
  onCancelConfig?: () => void;
  onInsertConfigBelow?: (index: number) => void;
  addingBelowConfigId?: string | null;
}

// Two-line header cell: bold config-level label on top, lighter run-level label below.
const StackedHeaderCell = ({
  top,
  bottom,
  className = '',
}: {
  top: ReactNode;
  bottom: ReactNode;
  className?: string;
}): ReactElement => (
  <div className={`cr-flex cr-min-w-0 cr-flex-col cr-justify-center cr-text-center ${className}`}>
    <div className="cr-font-bold">{top}</div>
    <div className="cr-font-normal">{bottom}</div>
  </div>
);

const SessionConfigsList = ({
  session,
  isEdit,
  configControls,
  showAllRuns,
  setShowAllRuns,
  updateNextConfigId,
  plannedRunConfigIds,
  setPlannedRunConfigIds,
  plannedRunNumbers,
  activeRunId,
  setActiveRunId,
  invalidateSession,
  reorderConfigLetters,
  configMovesToSave,
  changedConfigIds,
  setChangedConfigIds,
  editOpMode,
  setEditOpMode,
  onReprocessRun,
  onEditConfig,
  onSaveConfig,
  onCancelConfig,
  onInsertConfigBelow,
  addingBelowConfigId,
}: SessionConfigsListProps): ReactElement => {
  const configs = configControls;
  const { setValue, watch } = useFormContext();

  // Watch all config descriptions to detect duplicates in real-time
  const watchedConfigs = watch('configs') || [];

  // Find duplicate descriptions (only for non-deleted configs)
  const duplicateDescriptions = new Set<string>();
  const descriptionCounts = new Map<string, number>();

  watchedConfigs.forEach((config: any) => {
    if (!config.isDeleted && config.description && config.description.trim()) {
      const trimmedDescription = config.description.trim();
      const count = descriptionCounts.get(trimmedDescription) || 0;
      descriptionCounts.set(trimmedDescription, count + 1);
    }
  });

  descriptionCounts.forEach((count, description) => {
    if (count > 1) {
      duplicateDescriptions.add(description);
    }
  });

  // Function to swap configurations
  const handleSwapConfig = (index: number, direction: 'up' | 'down') => {
    if (isOperationLocked(editOpMode, 'reorder')) return;
    setEditOpMode('reorder');

    const currentConfigs = watch('configs');
    if (!currentConfigs) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= currentConfigs.length) return;

    // Create a copy of configs array
    const newConfigs = [...currentConfigs];

    // Swap the configurations
    [newConfigs[index], newConfigs[swapIndex]] = [newConfigs[swapIndex], newConfigs[index]];

    // Fix baseline references: if the config that moved up had the config that moved
    // down as its baseline, swap the baseline relationship
    const upperIndex = Math.min(index, swapIndex);
    const lowerIndex = Math.max(index, swapIndex);
    if (newConfigs[upperIndex].baseline_id === newConfigs[lowerIndex].id) {
      const oldUpperBaseline = newConfigs[lowerIndex].baseline_id;
      newConfigs[lowerIndex].baseline_id = newConfigs[upperIndex].id;
      newConfigs[upperIndex].baseline_id = oldUpperBaseline;
    }

    // Update form values
    setValue('configs', newConfigs, { shouldDirty: true });

    // Mark both configs as changed
    const newChangedIds = new Set(changedConfigIds);
    newChangedIds.add(newConfigs[index].id);
    newChangedIds.add(newConfigs[swapIndex].id);
    setChangedConfigIds(newChangedIds);

    // Reorder letters to maintain sequential ordering
    reorderConfigLetters();
  };

  return (
    <div className="cr-flex cr-flex-col">
      {/* `fr` tracks carry an automatic min-content floor, so below lg the
          declared ratios collapsed — the Cfg/Run # track shrank under its own
          label while the trailing 0.1fr spacer ballooned to ~62px. Narrow
          widths therefore size the two label columns to their content and let
          only the three value columns flex, with minmax(0,…) so they may
          shrink past min-content. The trailing track stays `auto` rather than a
          guessed fixed width — it carries the per-run report button, whose
          padding needs ~52px. From lg up there is room for the original ratios,
          which are kept verbatim. */}
      <div
        className={
          'cr-grid cr-text-center md:cr-m-2 '
          + 'cr-grid-cols-[2rem_auto_auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] '
          + 'lg:cr-grid-cols-[0.4fr_0.5fr_0.8fr_1.2fr_1.2fr_1.2fr_0.1fr]'
        }
      >
        {/* No horizontal padding: padding on a subgrid container insets its
            first and last tracks, which pushed the header's chevron column
            ~9px right of the same column in every row below it. */}
        <div
          className="cr-col-span-7 cr-mb-4 cr-grid cr-grid-cols-subgrid cr-sticky cr-top-10 cr-z-10 cr-rounded-lg cr-bg-white cr-py-3 cr-shadow-xl cr-text-xs cr-font-bold cr-uppercase cr-tracking-wider cr-text-cirrusBlue-400 cr-font-main md:cr-text-base cr-border-2 cr-border-cirrusBlue-100"
        >
          <div className="cr-min-w-0" onClick={() => setShowAllRuns(!showAllRuns)}>
            <AccordionIcon show={showAllRuns} />
          </div>
          <StackedHeaderCell top="Cfg" bottom="Run #" />
          <StackedHeaderCell
            className="cr-truncate"
            top={
              <>
                <span className="cr-inline md:cr-hidden">Par</span>
                <span className="cr-hidden md:cr-inline">Parent</span>
              </>
            }
            bottom="Time"
          />
          <div className="cr-min-w-0 cr-truncate">Drag</div>
          <div className="cr-min-w-0 cr-truncate cr-text-base md:cr-text-lg">+/-</div>
          <div className="cr-flex cr-min-w-0 cr-justify-end cr-truncate cr-pr-2">Next</div>
          <div className="cr-min-w-0 cr-truncate"></div>
        </div>
        {configs.map((config: any, index: number) => {
          const originalConfig = session.configs.find((c: any) => c?.id === config?.id);
          const watchedConfig = watchedConfigs[index];
          const trimmedDescription = watchedConfig?.description?.trim();
          const hasDuplicateDescription = trimmedDescription && duplicateDescriptions.has(trimmedDescription);

          return (
            <ConfigEntry
              index={index}
              key={config.fieldId}
              isEdit={isEdit}
              originalConfig={originalConfig}
              showAllRuns={showAllRuns}
              updateNextConfigId={updateNextConfigId}
              plannedRunConfigIds={plannedRunConfigIds}
              setPlannedRunConfigIds={setPlannedRunConfigIds}
              plannedRunNumbers={plannedRunNumbers}
              activeRunId={activeRunId}
              setActiveRunId={setActiveRunId}
              invalidateSession={invalidateSession}
              reorderConfigLetters={reorderConfigLetters}
              configMovesToSave={configMovesToSave}
              onSwapConfig={handleSwapConfig}
              changedConfigIds={changedConfigIds}
              setChangedConfigIds={setChangedConfigIds}
              hasDuplicateDescription={hasDuplicateDescription}
              editOpMode={editOpMode}
              setEditOpMode={setEditOpMode}
              onReprocessRun={onReprocessRun}
              onEditConfig={onEditConfig}
              onSaveConfig={onSaveConfig}
              onCancelConfig={onCancelConfig}
              onInsertConfigBelow={onInsertConfigBelow}
              addingBelowConfigId={addingBelowConfigId}
            />
          );
        })}
      </div>
    </div>
  );
};

export default SessionConfigsList;
